import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface MalakAttachment {
  url: string;
  name: string;
  type: string; // mime type
  size: number;
}

export interface MalakMessage {
  id: string;
  role: "user" | "assistant" | "agent";
  content: string;
  timestamp: Date;
  redirect?: string;
  action?: string;
  suggestedReplies?: string[];
  feedback?: -1 | 1 | null;
  failed?: boolean;
  retryText?: string;
  attachments?: MalakAttachment[];
  /** Structured data from backend (structured_quotes, booking_confirmed) — source of truth for pricing */
  metadata?: Record<string, any>;
}

export interface ProactiveHint {
  message: string;
  suggestedReplies?: string[];
}

export interface LiveRouteContext {
  tripId: string;
  routeName?: string;
  originCountry?: string;
  destinationCountry?: string;
  /** Courier's real-time GPS position */
  courierLat?: number;
  courierLng?: number;
  /** Active delivery cities for context */
  activeDeliveries?: Array<{ city: string; country: string; status: string }>;
}

interface MalakContextType {
  isOpen: boolean;
  open: () => void;
  openWithMessage: (message: string) => void;
  close: () => void;
  toggle: () => void;
  messages: MalakMessage[];
  addMessage: (msg: MalakMessage) => void;
  updateMessage: (id: string, updates: Partial<MalakMessage>) => void;
  clearMessages: () => void;
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  isLoading: boolean;
  setIsLoading: (v: boolean) => void;
  triggerProactive: (message: string, suggestedReplies?: string[]) => void;
  /** Mobile-only: hint stored without opening chat */
  setProactiveHint: (hint: ProactiveHint | null) => void;
  proactiveHint: ProactiveHint | null;
  consumeProactiveHint: () => void;
  /** True when an assistant message arrived while the chat was closed/unfocused. */
  hasUnreadAssistant: boolean;
  markAssistantRead: () => void;
  detectedLanguage: string | null;
  setDetectedLanguage: (lang: string | null) => void;
  isEscalated: boolean;
  setIsEscalated: (v: boolean) => void;
  escalationReason: string | null;
  setEscalationReason: (r: string | null) => void;
  pendingAutoMessage: string | null;
  clearPendingAutoMessage: () => void;
  /** Live Route Mode — set when courier is actively executing a trip */
  liveRouteMode: LiveRouteContext | null;
  setLiveRouteMode: (ctx: LiveRouteContext | null) => void;
}

const STORAGE_KEY_PREFIX = "malak_chat_";
const ANON_STORAGE_KEY = "malak_chat_anon";
const ANON_CONVERSATION_KEY = "malak_anon_conversation_id";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h — auto-expire old conversations
const MAX_MESSAGES = 50; // Keep only last 50 messages to prevent bloat

const MalakContext = createContext<MalakContextType | undefined>(undefined);

export const useMalak = () => {
  const ctx = useContext(MalakContext);
  if (!ctx) throw new Error("useMalak must be used within MalakProvider");
  return ctx;
};

function getStorageKey(userId: string | null): string {
  return userId ? `${STORAGE_KEY_PREFIX}${userId}` : ANON_STORAGE_KEY;
}

/**
 * Stable conversation id for anonymous users. Generated once per browser and
 * persisted in localStorage so the booking state machine on the backend
 * (`malak_booking_sessions.conversation_key`) sees the same key across every
 * turn — no more drift when the IP changes or the request is replayed.
 */
/**
 * Anonymous storage uses sessionStorage so memory does NOT persist after the
 * browser tab/session is closed. Within the same tab, the conversation id and
 * messages are remembered; once the tab/window is closed everything resets so
 * the backend booking state machine doesn't force-continue a stale flow.
 */
function getOrCreateAnonConversationId(): string {
  try {
    const existing = sessionStorage.getItem(ANON_CONVERSATION_KEY);
    if (existing) return existing;
    const id = `anon-${crypto.randomUUID()}`;
    sessionStorage.setItem(ANON_CONVERSATION_KEY, id);
    return id;
  } catch {
    return `anon-${crypto.randomUUID()}`;
  }
}

function getStore(userId: string | null): Storage {
  // Authenticated users → localStorage (cross-session memory)
  // Guests → sessionStorage (memory dies with the tab/session)
  if (typeof window === "undefined") return localStorage;
  return userId ? localStorage : sessionStorage;
}

const EMPTY_STATE = { messages: [], conversationId: null, isOpen: false, isEscalated: false, escalationReason: null };

function loadPersistedState(userId: string | null): { messages: MalakMessage[]; conversationId: string | null; isOpen: boolean; isEscalated: boolean; escalationReason: string | null } {
  // Anonymous visitors get a fresh, in-memory conversation every page load.
  // No sessionStorage restore, no localStorage restore — refresh = new chat,
  // new memory, new state-machine session. This matches the product rule:
  // "session based only unless logged in". Guests never carry stale booking
  // state across reloads.
  if (!userId) {
    try {
      sessionStorage.removeItem(ANON_STORAGE_KEY);
      sessionStorage.removeItem(ANON_CONVERSATION_KEY);
      localStorage.removeItem(ANON_STORAGE_KEY);
      localStorage.removeItem(ANON_CONVERSATION_KEY);
    } catch { /* ignore */ }
    return { ...EMPTY_STATE, conversationId: `anon-${crypto.randomUUID()}` };
  }
  try {
    const store = getStore(userId);
    const raw = store.getItem(getStorageKey(userId));
    if (!raw) {
      return EMPTY_STATE;
    }
    const parsed = JSON.parse(raw);
    // Auto-expire conversations older than 24h
    if (parsed.savedAt && Date.now() - parsed.savedAt > MAX_AGE_MS) {
      store.removeItem(getStorageKey(userId));
      return EMPTY_STATE;
    }
    // If previous session was escalated, start fresh (escalation is session-specific)
    const wasEscalated = parsed.isEscalated ?? false;
    return {
      messages: wasEscalated ? [] : (parsed.messages || []).slice(-MAX_MESSAGES).map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })),
      conversationId: wasEscalated ? null : (parsed.conversationId || null),
      isOpen: false, // never auto-open on reload
      isEscalated: false, // always reset escalation on reload
      escalationReason: null,
    };
  } catch {
    return EMPTY_STATE;
  }
}

function persistState(userId: string | null, messages: MalakMessage[], conversationId: string | null, isOpen: boolean, isEscalated: boolean, escalationReason: string | null) {
  // Anon = ephemeral. Never write anon chat state to any storage; refresh
  // must always start a fresh conversation with no memory.
  if (!userId) return;
  try {
    // Only keep last MAX_MESSAGES to prevent storage bloat
    const trimmed = messages.slice(-MAX_MESSAGES);
    getStore(userId).setItem(getStorageKey(userId), JSON.stringify({ messages: trimmed, conversationId, isOpen, isEscalated, escalationReason, savedAt: Date.now() }));
  } catch { /* quota exceeded — ignore */ }
}

export const MalakProvider = ({ children }: { children: ReactNode }) => {
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const prevUserIdRef = useRef<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<MalakMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
  const [isEscalated, setIsEscalated] = useState(false);
  const [escalationReason, setEscalationReason] = useState<string | null>(null);
  const [pendingAutoMessage, setPendingAutoMessage] = useState<string | null>(null);
  const [proactiveHint, setProactiveHint] = useState<ProactiveHint | null>(null);
  const [liveRouteMode, setLiveRouteMode] = useState<LiveRouteContext | null>(null);
  const [hasUnreadAssistant, setHasUnreadAssistant] = useState(false);

  // Listen for auth changes — load/clear state on login/logout
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      const uid = session?.user?.id || null;
      setCurrentUserId(uid);
      const state = loadPersistedState(uid);
      setMessages(state.messages);
      setConversationId(state.conversationId);
      setIsEscalated(state.isEscalated);
      setEscalationReason(state.escalationReason);
      prevUserIdRef.current = uid;
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id || null;
      // User changed (login/logout/switch)
      if (uid !== prevUserIdRef.current) {
        // On login, drop the anon conversation id so the guest state machine
        // session doesn't bleed into the authenticated flow.
        if (uid && !prevUserIdRef.current) {
          try {
            sessionStorage.removeItem("malak_anon_conversation_id");
            sessionStorage.removeItem("malak_chat_anon");
            // Legacy cleanup (older builds wrote anon state to localStorage)
            localStorage.removeItem("malak_anon_conversation_id");
            localStorage.removeItem("malak_chat_anon");
          } catch { /* ignore */ }
        }
        setCurrentUserId(uid);
        const state = loadPersistedState(uid);
        setMessages(state.messages);
        setConversationId(state.conversationId);
        setIsEscalated(state.isEscalated);
        setEscalationReason(state.escalationReason);
        setIsOpen(false);
        prevUserIdRef.current = uid;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Persist on every change (works for both anon + authenticated now)
  useEffect(() => {
    persistState(currentUserId, messages, conversationId, isOpen, isEscalated, escalationReason);
  }, [currentUserId, messages, conversationId, isOpen, isEscalated, escalationReason]);

  // One-time migration: purge legacy anon state from localStorage so guests
  // don't auto-restore old booking summaries on a fresh session.
  useEffect(() => {
    try {
      localStorage.removeItem("malak_chat_state");
      localStorage.removeItem("malak_chat_anon");
      localStorage.removeItem("malak_anon_conversation_id");
    } catch {}
  }, []);
  // Listen for open-malak custom event
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.message) {
        setPendingAutoMessage(detail.message);
      }
      setIsOpen(true);
    };
    window.addEventListener('open-malak', handler);
    return () => window.removeEventListener('open-malak', handler);
  }, []);

  // Listen for malak-trip-query events (from proactive alerts)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.query) {
        setPendingAutoMessage(detail.query);
        setIsOpen(true);
      }
    };
    window.addEventListener('malak-trip-query', handler);
    return () => window.removeEventListener('malak-trip-query', handler);
  }, []);

  const open = useCallback(() => setIsOpen(true), []);
  const openWithMessage = useCallback((message: string) => {
    setPendingAutoMessage(null);
    setIsOpen(true);

    window.setTimeout(() => {
      setPendingAutoMessage(message);
    }, 0);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  const addMessage = useCallback((msg: MalakMessage) => {
    setMessages((prev) => {
      if (prev.some(m => m.id === msg.id)) return prev;
      return [...prev, msg];
    });
  }, []);
  const updateMessage = useCallback((id: string, updates: Partial<MalakMessage>) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...updates } : m)));
  }, []);
  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setIsEscalated(false);
    setEscalationReason(null);
  }, []);

  const triggerProactive = useCallback((message: string, suggestedReplies?: string[]) => {
    setMessages((prev) => {
      if (prev.length > 0) return prev;
      const proactiveMsg: MalakMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: message,
        timestamp: new Date(),
        suggestedReplies,
      };
      return [proactiveMsg];
    });
    setIsOpen(true);
  }, []);

  const clearPendingAutoMessage = useCallback(() => setPendingAutoMessage(null), []);

  // Consume a proactive hint: inject it as a message and clear the hint
  const consumeProactiveHint = useCallback(() => {
    setProactiveHint((hint) => {
      if (!hint) return null;
      setMessages((prev) => {
        if (prev.length > 0) return prev;
        return [{
          id: crypto.randomUUID(),
          role: "assistant" as const,
          content: hint.message,
          timestamp: new Date(),
          suggestedReplies: hint.suggestedReplies,
        }];
      });
      return null;
    });
  }, []);

  const markAssistantRead = useCallback(() => {
    setHasUnreadAssistant(false);
    // Tell the service worker the chat is focused so push notifications are
    // suppressed while the user is reading.
    try {
      navigator.serviceWorker?.controller?.postMessage({
        type: "MALAK_CHAT_FOCUSED",
        focused: true,
      });
    } catch { /* ignore */ }
  }, []);

  // Clear unread + focus flag when chat opens; set focus=false when it closes.
  useEffect(() => {
    if (isOpen) {
      setHasUnreadAssistant(false);
      try {
        navigator.serviceWorker?.controller?.postMessage({
          type: "MALAK_CHAT_FOCUSED",
          focused: true,
        });
      } catch { /* ignore */ }
    } else {
      try {
        navigator.serviceWorker?.controller?.postMessage({
          type: "MALAK_CHAT_FOCUSED",
          focused: false,
        });
      } catch { /* ignore */ }
    }
  }, [isOpen]);

  // Realtime: assistant messages inserted in our conversation while the chat
  // is closed flip the unread flag so MalakPill shows a dot.
  useEffect(() => {
    if (!conversationId || !currentUserId) return;
    const channel = supabase
      .channel(`malak-assistant-${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "agent_messages",
          filter: `agent_conversation_id=eq.${conversationId}`,
        },
        (payload: any) => {
          if (payload?.new?.role !== "assistant") return;
          // Already visible — no need to badge.
          if (isOpen) return;
          setHasUnreadAssistant(true);
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, currentUserId, isOpen]);

  return (
    <MalakContext.Provider
      value={{
        isOpen, open, openWithMessage, close, toggle,
        messages, addMessage, updateMessage, clearMessages,
        conversationId, setConversationId,
        isLoading, setIsLoading,
        triggerProactive,
        proactiveHint, setProactiveHint, consumeProactiveHint,
        hasUnreadAssistant, markAssistantRead,
        detectedLanguage, setDetectedLanguage,
        isEscalated, setIsEscalated,
        escalationReason, setEscalationReason,
        pendingAutoMessage, clearPendingAutoMessage,
        liveRouteMode, setLiveRouteMode,
      }}
    >
      {children}
    </MalakContext.Provider>
  );
};
